import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import MultiplierScheduleCalendar from '../components/MultiplierScheduleCalendar';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const MULTIPLIER_OPTIONS = ['2.00', '3.00', '4.00', '5.00'];
const MULTIPLIER_CUSTOM = 'custom';
const MULTIPLIER_CUSTOM_VALUE = '1.00';
const SOURCE_OPTIONS = [
  { value: 'olive', label: 'Olive' },
  { value: 'wildfire', label: 'Wildfire' },
];
const LOOKUP_DEBOUNCE_MS = 400;

const HISTORY_ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'created', label: 'Added' },
  { value: 'multiplier_changed', label: 'Multiplier changed' },
  { value: 'activated', label: 'Activated' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'deleted', label: 'Removed' },
  { value: 'schedule_added', label: 'Schedule added' },
  { value: 'schedule_removed', label: 'Schedule removed' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatMultiplierLabel = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '—';
  if (num <= 1) return 'Custom by dates';
  return `${num.toFixed(2)}x`;
};

const getHistoryActionLabel = (action) => (
  HISTORY_ACTION_OPTIONS.find((option) => option.value === action)?.label || action
);

const getHistoryDetails = (entry) => {
  if (entry.action === 'created') {
    return `Added ${formatMultiplierLabel(entry.multiplier_value)} (${entry.active ? 'active' : 'inactive'})`;
  }
  if (entry.action === 'multiplier_changed') {
    return `${formatMultiplierLabel(entry.previous_multiplier)} → ${formatMultiplierLabel(entry.multiplier_value)}`;
  }
  if (entry.action === 'activated' || entry.action === 'deactivated') {
    return entry.action === 'activated' ? 'Inactive → Active' : 'Active → Inactive';
  }
  if (entry.action === 'deleted') {
    return `Removed ${formatMultiplierLabel(entry.multiplier_value)} (${entry.active ? 'was active' : 'was inactive'})`;
  }
  if (entry.action === 'schedule_added') {
    return entry.note ? `Added: ${entry.note}` : 'Schedule range added';
  }
  if (entry.action === 'schedule_removed') {
    return entry.note ? `Removed: ${entry.note}` : 'Schedule range removed';
  }
  return '—';
};

const getSourceLabel = (source) => (
  SOURCE_OPTIONS.find((option) => option.value === source)?.label || source
);

const formatMultiplierValue = (value) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return MULTIPLIER_OPTIONS[0];
  const normalized = num.toFixed(2);
  if (normalized === MULTIPLIER_CUSTOM_VALUE) return MULTIPLIER_CUSTOM_VALUE;
  return MULTIPLIER_OPTIONS.includes(normalized) ? normalized : MULTIPLIER_OPTIONS[0];
};

const MerchantInfoBlock = ({ source, merchant }) => {
  const displayName = merchant?.merchant_name || 'Unnamed merchant';
  const merchantId = merchant?.merchant_id || '';
  const domain = merchant?.domain || '';
  const logoUrl = merchant?.logo_url || '';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      {logoUrl && (
        <img
          src={logoUrl}
          alt=""
          style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8, background: '#fff', flexShrink: 0 }}
        />
      )}
      <div>
        <div style={{ fontWeight: 700, fontSize: '16px' }}>{displayName}</div>
        <div style={{ color: '#666', fontSize: '14px', marginTop: '4px' }}>
          {getSourceLabel(source)} · ID {merchantId}
          {domain ? ` · ${domain}` : ''}
        </div>
      </div>
    </div>
  );
};

const SuperuserPointMultipliers = () => {
  const navigate = useNavigate();
  const [multipliers, setMultipliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [merchantPreview, setMerchantPreview] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [formData, setFormData] = useState({
    source: 'olive',
    merchant_id: '',
    multiplier: '2.00',
    multiplierMode: 'constant',
    active: true,
  });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyFilters, setHistoryFilters] = useState({
    source: '',
    action: '',
    search: '',
  });
  const [expandedScheduleId, setExpandedScheduleId] = useState(null);
  const [schedulesMap, setSchedulesMap] = useState({});
  const [schedulesLoadingId, setSchedulesLoadingId] = useState(null);
  const [scheduleSavingId, setScheduleSavingId] = useState(null);
  const schedulesFetchedRef = useRef({});
  const [createFormPendingRanges, setCreateFormPendingRanges] = useState([]);
  const [createCalendarKey, setCreateCalendarKey] = useState(0);
  const [historyExpandedId, setHistoryExpandedId] = useState(null);
  const [historySchedules, setHistorySchedules] = useState({});

  const sortedMultipliers = useMemo(() => {
    return [...multipliers].sort((a, b) => {
      const sourceCompare = String(a.source || '').localeCompare(String(b.source || ''));
      if (sourceCompare !== 0) return sourceCompare;
      const aId = String(a.merchant_id || '').toLowerCase();
      const bId = String(b.merchant_id || '').toLowerCase();
      return aId.localeCompare(bId);
    });
  }, [multipliers]);

  const fetchMultipliers = useCallback(async () => {
    const response = await apiService.getAdminMerchantPointMultipliers();
    setMultipliers(Array.isArray(response?.results) ? response.results : []);
  }, []);

  const fetchHistory = useCallback(async (filters) => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (filters?.source) params.source = filters.source;
      if (filters?.action) params.action = filters.action;
      if (filters?.search?.trim()) params.search = filters.search.trim();
      const response = await apiService.getAdminMerchantPointMultipliersHistory(params);
      setHistory(Array.isArray(response?.results) ? response.results : []);
    } catch (err) {
      console.error('Failed to load point multiplier history:', err);
      setError('Failed to load point multiplier history.');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        await fetchMultipliers();
        await fetchHistory({});
      } catch (err) {
        console.error('Failed to load merchant point multipliers:', err);
        setError('Failed to load merchant point multipliers.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [fetchMultipliers, fetchHistory, navigate]);

  useEffect(() => {
    const merchantId = formData.merchant_id.trim();
    if (!merchantId) {
      setMerchantPreview(null);
      setLookupError('');
      setLookupLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLookupLoading(true);
    setLookupError('');

    const timer = setTimeout(async () => {
      try {
        const result = await apiService.lookupAdminMerchant({
          source: formData.source,
          merchantId,
        });
        if (cancelled) return;
        if (result?.found) {
          setMerchantPreview(result);
          setLookupError('');
        } else {
          setMerchantPreview(null);
          setLookupError('Merchant not found for this ID.');
        }
      } catch (err) {
        if (cancelled) return;
        setMerchantPreview(null);
        if (err?.response?.status === 404) {
          setLookupError('Merchant not found for this ID.');
        } else {
          setLookupError('Could not look up merchant. Try again.');
        }
      } finally {
        if (!cancelled) {
          setLookupLoading(false);
        }
      }
    }, LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.merchant_id, formData.source]);

  const canSave = Boolean(
    formData.merchant_id.trim()
    && merchantPreview?.merchant_name
    && !lookupLoading
    && !lookupError
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSave) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const saved = await apiService.upsertAdminMerchantPointMultiplier({
        source: formData.source,
        merchant_id: formData.merchant_id.trim(),
        merchant_name: merchantPreview.merchant_name,
        multiplier: formData.multiplierMode === MULTIPLIER_CUSTOM ? MULTIPLIER_CUSTOM_VALUE : formData.multiplier,
        active: formData.active,
      });

      if (createFormPendingRanges.length > 0 && saved?.id) {
        for (const range of createFormPendingRanges) {
          try {
            await apiService.createAdminMerchantPointMultiplierSchedule(saved.id, {
              start_date: range.start,
              end_date: range.end,
              multiplier_value: range.multiplierValue,
            });
          } catch (scheduleErr) {
            console.error('Failed to save schedule range during create:', scheduleErr);
          }
        }
      }

      setFormData({
        source: formData.source,
        merchant_id: '',
        multiplier: '2.00',
        multiplierMode: 'constant',
        active: true,
      });
      setMerchantPreview(null);
      setLookupError('');
      setCreateFormPendingRanges([]);
      setCreateCalendarKey((k) => k + 1);
      await fetchMultipliers();
      await fetchHistory(historyFilters);
      setSuccess('Multiplier saved.');
    } catch (err) {
      console.error('Failed to save merchant point multiplier:', err);
      const detail = err?.response?.data;
      setError(detail?.source?.[0] || detail?.merchant_id?.[0] || detail?.multiplier?.[0] || detail?.error || 'Failed to save multiplier.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    setError('');
    setSuccess('');
    setUpdatingId(item.id);

    try {
      await apiService.patchAdminMerchantPointMultiplier(item.id, { active: !item.active });
      await fetchMultipliers();
      await fetchHistory(historyFilters);
      setSuccess(item.active ? 'Multiplier deactivated.' : 'Multiplier activated.');
    } catch (err) {
      console.error('Failed to update merchant point multiplier:', err);
      setError(err?.response?.data?.error || 'Failed to update multiplier.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMultiplierChange = async (item, multiplier) => {
    const nextValue = formatMultiplierValue(multiplier);
    if (formatMultiplierValue(item.multiplier) === nextValue) return;

    const nextIsConstant = parseFloat(nextValue) > 1;
    if (nextIsConstant && expandedScheduleId === item.id) {
      setExpandedScheduleId(null);
    }

    setError('');
    setSuccess('');
    setUpdatingId(item.id);

    try {
      await apiService.patchAdminMerchantPointMultiplier(item.id, { multiplier: nextValue });
      await fetchMultipliers();
      await fetchHistory(historyFilters);
      setSuccess('Multiplier updated.');
    } catch (err) {
      console.error('Failed to update merchant point multiplier:', err);
      const detail = err?.response?.data;
      setError(detail?.multiplier?.[0] || detail?.error || 'Failed to update multiplier.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (item) => {
    const label = item.merchant_name || item.merchant_id || 'this merchant';
    if (!window.confirm(`Remove point multiplier for ${label}?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await apiService.deleteAdminMerchantPointMultiplier(item.id);
      await fetchMultipliers();
      await fetchHistory(historyFilters);
      setSuccess('Multiplier removed.');
    } catch (err) {
      console.error('Failed to delete merchant point multiplier:', err);
      setError(err?.response?.data?.error || 'Failed to remove multiplier.');
    }
  };

  const fetchSchedules = async (multiplierId) => {
    setSchedulesLoadingId(multiplierId);
    try {
      const response = await apiService.getAdminMerchantPointMultiplierSchedules(multiplierId);
      setSchedulesMap((prev) => ({ ...prev, [multiplierId]: response?.results ?? [] }));
      schedulesFetchedRef.current[multiplierId] = true;
    } catch (err) {
      console.error('Failed to fetch schedules:', err);
    } finally {
      setSchedulesLoadingId(null);
    }
  };

  const handleToggleSchedules = (multiplierId) => {
    if (expandedScheduleId === multiplierId) {
      setExpandedScheduleId(null);
      return;
    }
    setExpandedScheduleId(multiplierId);
    if (!schedulesFetchedRef.current[multiplierId]) {
      fetchSchedules(multiplierId);
    }
  };

  const handleAddScheduleRange = async (multiplierId, payload) => {
    setScheduleSavingId(multiplierId);
    try {
      await apiService.createAdminMerchantPointMultiplierSchedule(multiplierId, payload);
      await fetchSchedules(multiplierId);
      setSuccess('Schedule saved.');
    } finally {
      setScheduleSavingId(null);
    }
  };

  const handleDeleteSchedule = async (multiplierId, scheduleId) => {
    try {
      await apiService.deleteAdminMerchantPointMultiplierSchedule(multiplierId, scheduleId);
      await fetchSchedules(multiplierId);
      setSuccess('Schedule removed.');
    } catch (err) {
      console.error('Failed to delete schedule:', err);
      setError('Failed to remove schedule.');
    }
  };

  const handleToggleHistoryMore = async (entry) => {
    if (!entry.multiplier_id) return;
    const key = entry.multiplier_id;
    if (historyExpandedId === entry.id) {
      setHistoryExpandedId(null);
      return;
    }
    setHistoryExpandedId(entry.id);
    if (!historySchedules[key]) {
      try {
        const res = await apiService.getAdminMerchantPointMultiplierSchedules(key);
        setHistorySchedules((prev) => ({ ...prev, [key]: res?.results ?? [] }));
      } catch (err) {
        console.error('Failed to fetch schedules for history entry:', err);
        setHistorySchedules((prev) => ({ ...prev, [key]: [] }));
      }
    }
  };

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton to="/superuser/dashboard/" text="Back to Superuser Dashboard" />
          <h1 className="staff-page-header">Point Multipliers</h1>
          <p className="staff-page-subheader">
            Enter an Olive offer ID or Wildfire merchant ID to set a points multiplier.
          </p>

          {error && (
            <div style={{ marginTop: '20px', color: '#b00020' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ marginTop: '20px', color: '#0b7a28' }}>
              {success}
            </div>
          )}

          <div className="add-reward-section">
            <form onSubmit={handleSubmit} className="reward-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Source</label>
                  <select
                    className="form-select"
                    value={formData.source}
                    onChange={(event) => setFormData({ ...formData, source: event.target.value })}
                  >
                    {SOURCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {formData.source === 'olive' ? 'Olive Offer ID' : 'Wildfire Merchant ID'}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.merchant_id}
                    onChange={(event) => setFormData({ ...formData, merchant_id: event.target.value })}
                    required
                  />
                </div>
              </div>

              {(lookupLoading || merchantPreview || lookupError) && (
                <div
                  style={{
                    padding: '14px 16px',
                    border: `1px solid ${lookupError ? '#f5c2c7' : '#e9ecef'}`,
                    borderRadius: '12px',
                    background: lookupError ? '#fff5f5' : '#f8f9fa',
                  }}
                >
                  {lookupLoading && (
                    <div style={{ color: '#666' }}>Looking up merchant…</div>
                  )}
                  {!lookupLoading && merchantPreview && (
                    <MerchantInfoBlock source={formData.source} merchant={merchantPreview} />
                  )}
                  {!lookupLoading && lookupError && (
                    <div style={{ color: '#b00020' }}>{lookupError}</div>
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Multiplier</label>
                  <select
                    className="form-select"
                    value={formData.multiplierMode === MULTIPLIER_CUSTOM ? MULTIPLIER_CUSTOM : formData.multiplier}
                    onChange={(event) => {
                      const val = event.target.value;
                      if (val === MULTIPLIER_CUSTOM) {
                        setFormData({ ...formData, multiplierMode: MULTIPLIER_CUSTOM });
                        setCreateCalendarKey((k) => k + 1);
                      } else {
                        setFormData({ ...formData, multiplier: val, multiplierMode: 'constant' });
                        setCreateFormPendingRanges([]);
                        setCreateCalendarKey((k) => k + 1);
                      }
                    }}
                  >
                    {MULTIPLIER_OPTIONS.map((value) => (
                      <option key={value} value={value}>
                        {parseFloat(value)}× — Constant while active
                      </option>
                    ))}
                    <option value={MULTIPLIER_CUSTOM}>Custom by dates</option>
                  </select>
                </div>
              </div>

              {formData.multiplierMode === MULTIPLIER_CUSTOM && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: '8px',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    fontSize: '13px',
                    color: '#92400e',
                  }}
                >
                  Custom by dates — the multiplier only applies during the date ranges you select below. On all other days this merchant earns standard points.
                </div>
              )}

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  id="merchant-multiplier-active"
                  type="checkbox"
                  checked={formData.active}
                  onChange={(event) => setFormData({ ...formData, active: event.target.checked })}
                />
                <label htmlFor="merchant-multiplier-active" className="form-label" style={{ marginBottom: 0 }}>
                  Active
                </label>
              </div>

              {formData.multiplierMode === MULTIPLIER_CUSTOM && (
                <div style={{ marginTop: '4px' }}>
                  <MultiplierScheduleCalendar
                    key={createCalendarKey}
                    existingSchedules={[]}
                    saving={false}
                    embeddedMode
                    onPendingRangesChange={setCreateFormPendingRanges}
                  />
                </div>
              )}

              <button type="submit" className="staff-page-button" disabled={saving || !canSave}>
                <span className="staff-page-button-text">
                  {saving
                    ? 'Saving...'
                    : createFormPendingRanges.length > 0
                      ? `Save Multiplier + ${createFormPendingRanges.length} schedule${createFormPendingRanges.length > 1 ? 's' : ''}`
                      : 'Save Multiplier'}
                </span>
              </button>
            </form>
          </div>

          <div className="add-reward-section">
            <h2 className="staff-section-header">Current Multipliers</h2>
            {loading ? (
              <p className="staff-page-content">Loading...</p>
            ) : sortedMultipliers.length === 0 ? (
              <p className="staff-page-content">No point multipliers configured.</p>
            ) : (
              <div style={{ display: 'grid', gap: '12px', marginTop: '20px' }}>
                {sortedMultipliers.map((item) => {
                  const isExpanded = expandedScheduleId === item.id;
                  const schedules = schedulesMap[item.id] ?? [];
                  const scheduleCount = schedulesFetchedRef.current[item.id] ? schedules.length : null;
                  const isCustomMode = parseFloat(item.multiplier) <= 1;

                  return (
                    <div
                      key={item.id}
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto auto auto auto',
                          gap: '12px 16px',
                          alignItems: 'center',
                          padding: '16px',
                        }}
                      >
                        <MerchantInfoBlock
                          source={item.source}
                          merchant={{
                            merchant_id: item.merchant_id,
                            merchant_name: item.merchant_name,
                            logo_url: item.logo_url,
                            domain: item.domain,
                          }}
                        />
                        <select
                          className="form-select"
                          value={isCustomMode ? MULTIPLIER_CUSTOM : formatMultiplierValue(item.multiplier)}
                          disabled={updatingId === item.id}
                          onChange={(event) => {
                            const val = event.target.value;
                            handleMultiplierChange(item, val === MULTIPLIER_CUSTOM ? MULTIPLIER_CUSTOM_VALUE : val);
                          }}
                          style={{ minWidth: '136px' }}
                          aria-label="Points multiplier"
                        >
                          {MULTIPLIER_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                              {parseFloat(value)}× — Constant while active
                            </option>
                          ))}
                          <option value={MULTIPLIER_CUSTOM}>Custom by dates</option>
                        </select>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input
                            id={`multiplier-active-${item.id}`}
                            type="checkbox"
                            checked={Boolean(item.active)}
                            disabled={updatingId === item.id}
                            onChange={() => handleToggleActive(item)}
                          />
                          <label htmlFor={`multiplier-active-${item.id}`} style={{ margin: 0, fontSize: '14px' }}>
                            {updatingId === item.id ? 'Saving…' : (item.active ? 'Active' : 'Inactive')}
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => isCustomMode && handleToggleSchedules(item.id)}
                          disabled={!isCustomMode}
                          title={isCustomMode ? 'Manage date schedules' : 'Switch to "Custom by dates" to manage schedules'}
                          style={{
                            padding: '8px 14px',
                            border: `1px solid ${!isCustomMode ? '#e9ecef' : isExpanded ? '#3b82f6' : '#dee2e6'}`,
                            borderRadius: '100px',
                            background: !isCustomMode ? '#f5f5f5' : isExpanded ? '#eff6ff' : '#fff',
                            fontSize: '13px',
                            color: !isCustomMode ? '#bbb' : isExpanded ? '#1d4ed8' : '#555',
                            cursor: isCustomMode ? 'pointer' : 'not-allowed',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                          }}
                        >
                          {schedulesLoadingId === item.id ? '…' : '📅'}
                          {scheduleCount !== null ? ` ${scheduleCount}` : ''}
                          {isCustomMode && (isExpanded ? ' ▲' : ' ▼')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          style={{
                            padding: '8px 16px',
                            border: '1px solid #dee2e6',
                            borderRadius: '100px',
                            background: '#fff',
                            fontSize: '14px',
                            color: '#666',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      {isExpanded && (
                        <div
                          style={{
                            padding: '20px 20px 24px',
                            borderTop: '1px solid #f0f0f0',
                            background: '#fafafa',
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '16px', color: '#333' }}>
                            Date schedules — {item.merchant_name || item.merchant_id}
                          </div>
                          {schedulesLoadingId === item.id ? (
                            <div style={{ fontSize: '14px', color: '#666' }}>Loading schedules…</div>
                          ) : (
                            <MultiplierScheduleCalendar
                              existingSchedules={schedules}
                              saving={scheduleSavingId === item.id}
                              onAddRange={(payload) => handleAddScheduleRange(item.id, payload)}
                              onDeleteSchedule={(scheduleId) => handleDeleteSchedule(item.id, scheduleId)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="add-reward-section">
            <h2 className="staff-section-header">History</h2>
            <p className="staff-page-content" style={{ marginTop: '8px' }}>
              Who added or changed multipliers, when, and active status updates.
            </p>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <select
                className="form-select"
                value={historyFilters.source}
                onChange={(event) => setHistoryFilters((current) => ({ ...current, source: event.target.value }))}
                style={{ minWidth: '140px' }}
              >
                <option value="">All sources</option>
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                className="form-select"
                value={historyFilters.action}
                onChange={(event) => setHistoryFilters((current) => ({ ...current, action: event.target.value }))}
                style={{ minWidth: '180px' }}
              >
                {HISTORY_ACTION_OPTIONS.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-input"
                placeholder="Search merchant or user..."
                value={historyFilters.search}
                onChange={(event) => setHistoryFilters((current) => ({ ...current, search: event.target.value }))}
                style={{ minWidth: '220px' }}
              />
              <button
                type="button"
                className="staff-page-button"
                onClick={() => fetchHistory(historyFilters)}
                disabled={historyLoading}
              >
                <span className="staff-page-button-text">{historyLoading ? 'Loading...' : 'Apply'}</span>
              </button>
            </div>

            {historyLoading ? (
              <p className="staff-page-content">Loading history...</p>
            ) : history.length === 0 ? (
              <p className="staff-page-content">No history yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e9ecef', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Date & time</th>
                      <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>User</th>
                      <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Merchant</th>
                      <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Action</th>
                      <th style={{ padding: '12px 8px', fontSize: '12px', color: '#666' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((entry) => {
                      const isCustomEntry = parseFloat(entry.multiplier_value) <= 1;
                      const isExpanded = historyExpandedId === entry.id;
                      const schedKey = entry.multiplier_id;
                      const entrySchedules = schedKey ? (historySchedules[schedKey] ?? null) : null;

                      return (
                        <>
                          <tr key={entry.id} style={{ borderBottom: isExpanded ? 'none' : '1px solid #eee' }}>
                            <td style={{ padding: '12px 8px', fontSize: '14px', whiteSpace: 'nowrap' }}>
                              {formatDateTime(entry.created_at)}
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                              <div>{entry.changed_by_name || 'Unknown user'}</div>
                              {entry.changed_by_email && (
                                <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                  {entry.changed_by_email}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                              <div>{entry.merchant_name || 'Unnamed merchant'}</div>
                              <div style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                                {getSourceLabel(entry.source)} · ID {entry.merchant_id}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                              {getHistoryActionLabel(entry.action)}
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '14px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <span>{getHistoryDetails(entry)}</span>
                                {isCustomEntry && entry.multiplier_id && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleHistoryMore(entry)}
                                    style={{
                                      padding: '2px 10px',
                                      border: `1px solid ${isExpanded ? '#3b82f6' : '#d1d5db'}`,
                                      borderRadius: '100px',
                                      background: isExpanded ? '#eff6ff' : '#fff',
                                      color: isExpanded ? '#1d4ed8' : '#555',
                                      fontSize: '12px',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {isExpanded ? 'Hide ▲' : 'Schedules ▼'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`${entry.id}-schedules`} style={{ borderBottom: '1px solid #eee' }}>
                              <td colSpan={5} style={{ padding: '0 16px 20px 16px', background: '#fafafa' }}>
                                {entrySchedules === null ? (
                                  <div style={{ fontSize: '13px', color: '#888', padding: '12px 0' }}>Loading schedules…</div>
                                ) : entrySchedules.length === 0 ? (
                                  <div style={{ fontSize: '13px', color: '#aaa', padding: '12px 0', fontStyle: 'italic' }}>No scheduled date ranges.</div>
                                ) : (
                                  <div style={{ paddingTop: '16px' }}>
                                    <MultiplierScheduleCalendar
                                      existingSchedules={entrySchedules}
                                      readOnly
                                    />
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperuserPointMultipliers;
